import { trackSectionCreated } from '@/components/tiptap/markdown-native/section-node/section-analytics';
import { getSectionEditorContext } from '@/components/tiptap/markdown-native/section-node/section-editor-context';
import { insertDraftSectionAt } from '@/components/tiptap/shared/insert-draft-section';
import { createDictationGroup } from '@/components/tiptap/sharedExtensions/slash-command/dictation-group';
import { createProtocolGroup } from '@/components/tiptap/sharedExtensions/slash-command/protocol-group';
import { createSnippetGroup } from '@/components/tiptap/sharedExtensions/slash-command/snippet-group';
import {
  type Command,
  type Group,
} from '@/components/tiptap/sharedExtensions/slash-command/types';
import { fixCursorIfSafari } from '@/components/tiptap/utils/fixSafariCursor';
import { TypographyP2 } from '@heidi/design-system/typography';
import { type Editor } from '@tiptap/react';
import { List, ListOrdered, Minus, PlusSquare, Table } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

/**
 * Block-insert commands. All are in `buildMarkdownNativeExtensions` and
 * round-trip cleanly, so none are flag-gated (unlike the vMarkdown twin).
 * Code block is deliberately absent — see the extension kit's
 * `readOnlyCodeBlock`.
 */
export function getInsertCommands(): Command[] {
  return [
    {
      name: 'bulletList',
      label: 'Bullet list',
      icon: List,
      action: (ed: Editor) => {
        ed.chain().focus().toggleBulletList().run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Bullet list" id="drh2NUuJm0" />
        </TypographyP2>
      ),
    },
    {
      name: 'orderedList',
      label: 'Numbered list',
      icon: ListOrdered,
      action: (ed: Editor) => {
        ed.chain().focus().toggleOrderedList().run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Numbered list" id="lbsCQSfTG9" />
        </TypographyP2>
      ),
    },
    {
      name: 'horizontalRule',
      label: 'Horizontal Rule',
      icon: Minus,
      action: (ed: Editor) => {
        ed.chain().focus().setHorizontalRule().run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Horizontal Rule" id="1uhB/CqNka" />
        </TypographyP2>
      ),
    },
    {
      name: 'table',
      label: 'Table',
      icon: Table,
      action: (ed: Editor) => {
        ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Table" id="IeBejt65iX" />
        </TypographyP2>
      ),
    },
  ];
}

export function getDefaultGroups(editor: Editor): Group[] {
  const groups: Group[] = [];

  const dictationGroup = createDictationGroup(editor);
  if (dictationGroup) {
    groups.push(dictationGroup);
  }

  groups.push({
    name: 'insert',
    title: 'INSERT',
    commands: [
      {
        name: 'newSection',
        label: 'New Section',
        icon: PlusSquare,
        action: (ed: Editor) => {
          // Insert after the enclosing section if the cursor sits inside one,
          // otherwise at the cursor (parity with the vMarkdown slash command).
          const { $from } = ed.state.selection;
          const insertPos =
            $from.depth >= 1 && $from.node(1).type.name === 'sectionNode'
              ? $from.after(1)
              : $from.pos;

          insertDraftSectionAt(ed, insertPos);

          // `editor_context` is derived from the editor type stamped on section
          // storage (note vs document), not imported from vMarkdown. Dispatched
          // via the `store` singleton because `getGroups` is a plain factory,
          // not a React hook.
          trackSectionCreated(getSectionEditorContext(ed), 'slash_command');
        },
        content: (
          <TypographyP2>
            <FormattedMessage defaultMessage="New Section" id="9Pr/cVX7pr" />
          </TypographyP2>
        ),
      },
      ...getInsertCommands(),
    ],
  });

  return groups;
}

export function getGroups(editor: Editor): Group[] {
  // Multi-line snippet content is GFM markdown; insert through the
  // `@tiptap/markdown` boundary, never the legacy regex serializer.
  const snippets = createSnippetGroup({
    editor,
    createInsertAction: (ed, content) => {
      ed.commands.insertContent(content, { contentType: 'markdown' });
      fixCursorIfSafari(ed);
    },
  });

  // Protocols insert a `/shorthand ` token at the cursor for chat invocation.
  const protocols = createProtocolGroup({
    editor,
    createInsertAction: (ed, shorthand) => {
      ed.commands.insertContent(`/${shorthand} `);
      fixCursorIfSafari(ed);
    },
  });

  const groups: Group[] = [];
  if (protocols) {
    groups.unshift(protocols);
  }
  groups.push(...getDefaultGroups(editor), snippets);
  return groups;
}
